import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { getContext } from '@fiverr-private/fiverr_context';
import ProfileImage from '@fiverr-private/profile_image';
import { DEFAULT_LOCALE, USER_IMAGE_SIZE, HIGHLIGHTED_REVIEW_ITEM_DESCRIPTION_MAX_LENGTH } from '../../../constants';
import { shouldDisplayTranslation } from '../../../utils/translation';
import { dateStringToHowLongAgo, getPackageEntryClassNames } from '../../../utils';
import useTranslation from '../../shared/hooks/useTranslation';
import Summarize from '../../shared/Summarize';
import Country from '../../shared/ReviewerSubDetails/Country';
import Rating, { THEMES } from '../../shared/Rating';
import Description from '../../shared/Description';
import InlineDivider from '../../shared/InlineDivider';

import './style.scss';

const componentName = 'gig_review';
export const LINE_HEIGHT = 24;

export const ReviewItem = ({
    username,
    reviewer_country_code: countryCode,
    user_image_url: userImageUrl,
    created_at: reviewCreationDate,
    comment,
    value,
    comment_language: language,
    maxLines,
    withTranslation,
    withExtendedDescription,
    isShortDetails = false,
    className = '',
    resetDescription,
}) => {
    const { locale = DEFAULT_LOCALE } = getContext();
    const displayTranslationButton = shouldDisplayTranslation(locale, language);
    const { translationConfig, text } = useTranslation(comment, language);
    const createdAt = reviewCreationDate ? dateStringToHowLongAgo(reviewCreationDate, locale) : null;
    const reviewItemStyle = {
        height: maxLines * LINE_HEIGHT,
        WebkitLineClamp: maxLines,
    };

    const user = {
        has_profile_photo: !!userImageUrl,
        username,
        profile_photo: userImageUrl,
    };

    const containerClassname = getPackageEntryClassNames(`carousel-review-item ${className}`);

    return (
        <div className={containerClassname}>
            <ProfileImage user={user} profileImageSize={USER_IMAGE_SIZE} isImageLazy />

            <div className="details">
                <div className="reviewer">
                    <h6 className="text-display-7">{username}</h6>
                    {countryCode && <Country code={countryCode} isShortDetails={isShortDetails} />}
                    <InlineDivider />
                    <Rating
                        score={value}
                        theme={isShortDetails ? THEMES.ONE_STAR : THEMES.DYNAMIC}
                        className={classnames({ 'p-l-0': !isShortDetails })}
                    />
                </div>
                {withExtendedDescription ? (
                    <Description
                        text={text}
                        maxLength={HIGHLIGHTED_REVIEW_ITEM_DESCRIPTION_MAX_LENGTH}
                        className="m-0 review-item-description"
                        displayLess
                        resetExpanded={resetDescription}
                    />
                ) : (
                    <p className="review-item-comment" style={reviewItemStyle}>
                        {text}
                    </p>
                )}
                {Boolean(withTranslation || createdAt) && (
                    <Summarize
                        displayTranslationButton={displayTranslationButton}
                        createdAt={createdAt}
                        translationConfig={translationConfig}
                        componentName={componentName}
                    />
                )}
            </div>
        </div>
    );
};

ReviewItem.defaultProps = {
    comment_language: '',
    maxLines: 2,
    withExtendedDescription: false,
    resetDescription: false,
};

ReviewItem.propTypes = {
    username: PropTypes.string.isRequired,
    reviewer_country_code: PropTypes.string,
    user_image_url: PropTypes.string,
    comment: PropTypes.string,
    value: PropTypes.number,
    maxLines: PropTypes.number,
    comment_language: PropTypes.string,
    withTranslation: PropTypes.bool,
    withExtendedDescription: PropTypes.bool,
    className: PropTypes.string,
    resetDescription: PropTypes.bool,
};

export default ReviewItem;
